import type SaveLoadData from '@/model/GameSystemData/SaveLoadData'
import type { SettingsSaveLoadDraft } from '@/features/workspace/settings/types'

/** 存读档设置里字号步进器的最小合法值。 */
export const SAVE_LOAD_FONT_MIN_SIZE = 12

/**
 * 设置弹窗预览用字号：`CundangFontSize` 为 0 时跟随剧情主字号，仅用于展示。
 * @param draftFontSize 存读档 draft 字号（0 表示未单独配置）
 * @param mainFontSize 剧情主字号
 */
export function resolveSaveLoadPreviewFontSize(draftFontSize: number, mainFontSize: number): number {
  const raw = Number(draftFontSize ?? 0)
  if (raw >= SAVE_LOAD_FONT_MIN_SIZE) return raw
  return Math.max(SAVE_LOAD_FONT_MIN_SIZE, Number(mainFontSize || 30))
}

/**
 * 字号步进器展示值：未配置时显示剧情字号，便于预览，但不代表已写入工程。
 */
export function resolveSaveLoadStepperDisplaySize(draftFontSize: number, previewFontSize: number): number {
  const raw = Number(draftFontSize ?? 0)
  return raw >= SAVE_LOAD_FONT_MIN_SIZE ? raw : previewFontSize
}

/**
 * 用户在本轮设置中修改字体后，写回 `Sldata` 的字号；小于 12 视为 0（跟随剧情）。
 */
export function resolveCommittedSaveLoadFontSize(draftFontSize: number): number {
  const rawFontSize = Math.floor(Number(draftFontSize))
  return Number.isFinite(rawFontSize) && rawFontSize >= SAVE_LOAD_FONT_MIN_SIZE ? rawFontSize : 0
}

/**
 * 写回存读档字体字段：未改字体控件时保留工程原值，避免改档位等布局时误改字号。
 * @param target 待写入的 `SaveLoadData`
 * @param draft 当前存读档 draft
 * @param preservedFont 保留字体时为工程内原 `Sldata` 字体字段，否则为 null
 */
export function applySaveLoadFontFields(
  target: SaveLoadData,
  draft: Pick<SettingsSaveLoadDraft, 'Font' | 'FontSize'>,
  preservedFont: Pick<SaveLoadData, 'CundangFontName' | 'CundangFontSize'> | null,
): void {
  if (preservedFont) {
    target.CundangFontName = preservedFont.CundangFontName
    target.CundangFontSize = preservedFont.CundangFontSize
    return
  }
  target.CundangFontName = draft.Font
  target.CundangFontSize = resolveCommittedSaveLoadFontSize(draft.FontSize)
}
